import React from 'react';
import {
  Plus,
  Search,
  Filter as FilterIcon,
  ChevronDown,
  FolderOpen,
  Play,
  CheckCircle2,
  ExternalLink,
  Eye,
  Edit,
  Trash2,
  Archive,
  X as XIcon,
  User,
  Users,
  Globe,
  Smartphone,
  Megaphone,
  Rocket,
  Layers,
} from 'lucide-react';
import {
  StatCard,
  StatusPill,
  AvatarStack,
  ProgressBar,
  TablePagination,
  RowActionsMenu,
} from './ui';
import {
  formatLastActivity,
  projectIconTone,
  projectDisplayStatus,
  projectProgress,
} from './ui/formatters';

const ICON_CYCLE = [Globe, Smartphone, Megaphone, Rocket, Layers];

const BILLABLE_OPTIONS = [
  { id: 'true', name: 'Billable Only' },
  { id: 'false', name: 'Non-billable Only' },
];

const DOCUMENTS_OPTIONS = [
  { id: 'true', name: 'With Documents' },
  { id: 'false', name: 'Without Documents' },
];

const projectIcon = (seed = '') => {
  const str = String(seed);
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) hash += str.charCodeAt(i);
  return ICON_CYCLE[hash % ICON_CYCLE.length];
};

const ProjectsListView = ({
  user,
  title = 'Projects',
  subtitle,
  projectScope = 'all',
  onProjectScopeChange,
  searchTerm,
  onSearchChange,
  showFilters,
  onToggleFilters,
  filters,
  onFilterChange,
  onClearFilters,
  teamMembers,
  projects = [],
  filteredProjects,
  paginatedProjects,
  currentPage,
  pageSize,
  pageSizeOptions,
  totalPages,
  onPageChange,
  onPageSizeChange,
  onCreate,
  onView,
  onOpenInNewWindow,
  onEdit,
  onDelete,
  showDeletedProjects,
  onToggleDeleted,
  deletedProjectsCount,
  deletedProjectsLoading,
  deletedProjects,
  onRestore,
  canManageProject,
}) => {
  const [openFilterDropdown, setOpenFilterDropdown] = React.useState(null);
  const [clientDropdownSearch, setClientDropdownSearch] = React.useState('');
  const [billableDropdownSearch, setBillableDropdownSearch] = React.useState('');
  const [memberDropdownSearch, setMemberDropdownSearch] = React.useState('');
  const [documentsDropdownSearch, setDocumentsDropdownSearch] = React.useState('');

  const membersById = React.useMemo(() => {
    const map = new Map();
    (teamMembers || []).forEach((m) => map.set(m.id, m));
    return map;
  }, [teamMembers]);

  const clientOptions = React.useMemo(() => {
    const names = new Set();
    (projects || []).forEach((p) => {
      const client = (p.client || '').trim();
      if (client) names.add(client);
    });
    return Array.from(names)
      .sort((a, b) => a.localeCompare(b))
      .map((name) => ({ id: name, name }));
  }, [projects]);

  const filteredClientsForDropdown = React.useMemo(() => {
    if (!clientDropdownSearch.trim()) return clientOptions;
    const term = clientDropdownSearch.toLowerCase().trim();
    return clientOptions.filter((c) => c.name.toLowerCase().includes(term));
  }, [clientOptions, clientDropdownSearch]);

  const filteredBillableForDropdown = React.useMemo(() => {
    if (!billableDropdownSearch.trim()) return BILLABLE_OPTIONS;
    const term = billableDropdownSearch.toLowerCase().trim();
    return BILLABLE_OPTIONS.filter((o) => o.name.toLowerCase().includes(term));
  }, [billableDropdownSearch]);

  const filteredMembersForDropdown = React.useMemo(() => {
    const list = teamMembers || [];
    if (!memberDropdownSearch.trim()) return list;
    const term = memberDropdownSearch.toLowerCase().trim();
    return list.filter(
      (m) =>
        (m.full_name || '').toLowerCase().includes(term) ||
        (m.username || '').toLowerCase().includes(term) ||
        (m.email || '').toLowerCase().includes(term)
    );
  }, [teamMembers, memberDropdownSearch]);

  const filteredDocumentsForDropdown = React.useMemo(() => {
    if (!documentsDropdownSearch.trim()) return DOCUMENTS_OPTIONS;
    const term = documentsDropdownSearch.toLowerCase().trim();
    return DOCUMENTS_OPTIONS.filter((o) => o.name.toLowerCase().includes(term));
  }, [documentsDropdownSearch]);

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (openFilterDropdown && !event.target.closest('.filter-dropdown')) {
        setOpenFilterDropdown(null);
        setClientDropdownSearch('');
        setBillableDropdownSearch('');
        setMemberDropdownSearch('');
        setDocumentsDropdownSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openFilterDropdown]);

  const renderFilterDropdown = (
    type,
    label,
    currentValue,
    displayValue,
    onSelect,
    searchValue,
    onSearchChange,
    filteredItems,
    allLabel
  ) => (
    <div className="w-full relative filter-dropdown">
      <label className="text-xs font-medium text-secondary-600 dark:text-secondary-400 mb-1 block">
        {label}
      </label>
      <button
        type="button"
        onClick={() => setOpenFilterDropdown(openFilterDropdown === type ? null : type)}
        className="input-field w-full text-left flex items-center justify-between gap-2"
      >
        <span className="truncate text-sm">{displayValue || allLabel || `All ${label}`}</span>
        <ChevronDown
          className={`h-4 w-4 flex-shrink-0 text-secondary-400 transition-transform ${
            openFilterDropdown === type ? 'rotate-180' : ''
          }`}
        />
      </button>
      {openFilterDropdown === type && (
        <div className="absolute z-30 mt-1 w-full bg-white dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-700 rounded-lg shadow-lg max-h-72 flex flex-col">
          <div className="p-2 border-b border-secondary-100 dark:border-secondary-700 sticky top-0 bg-white dark:bg-secondary-800 rounded-t-lg">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-secondary-400" />
              <input
                type="text"
                placeholder={`Search ${label.toLowerCase()}...`}
                className="input-field w-full !pl-9"
                value={searchValue}
                onChange={(e) => onSearchChange(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                autoFocus
              />
            </div>
          </div>
          <div className="overflow-y-auto flex-1 min-h-0">
            <button
              type="button"
              onClick={() => {
                onSelect('');
                setOpenFilterDropdown(null);
                onSearchChange('');
              }}
              className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                !currentValue
                  ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 font-medium'
                  : 'text-secondary-700 dark:text-secondary-300 hover:bg-secondary-50 dark:hover:bg-secondary-700/50'
              }`}
            >
              {allLabel || `All ${label}`}
            </button>
            {filteredItems.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => {
                  onSelect(item.id);
                  setOpenFilterDropdown(null);
                  onSearchChange('');
                }}
                className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                  String(currentValue) === String(item.id)
                    ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 font-medium'
                    : 'text-secondary-700 dark:text-secondary-300 hover:bg-secondary-50 dark:hover:bg-secondary-700/50'
                }`}
              >
                {item.name || item.full_name || item.username}
              </button>
            ))}
            {filteredItems.length === 0 && (
              <div className="px-4 py-3 text-sm text-secondary-500 dark:text-secondary-400">
                No matches
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  const activeCount = filteredProjects.filter((p) => {
    const s = projectDisplayStatus(p);
    return s === 'In Progress' || s === 'On Hold';
  }).length;
  const completedCount = filteredProjects.filter((p) => projectDisplayStatus(p) === 'Completed').length;
  const hasActiveFilters = Object.values(filters || {}).some((v) => v !== '');

  const resolveMembers = (ids) =>
    (ids || []).map((id) => membersById.get(id) || { id, full_name: 'Member' });

  const selectedMember = (teamMembers || []).find(
    (m) => String(m.id) === String(filters.member_id)
  );
  const selectedBillable = BILLABLE_OPTIONS.find((o) => o.id === filters.is_billable);
  const selectedDocuments = DOCUMENTS_OPTIONS.find((o) => o.id === filters.has_documents);

  return (
    <div className="space-y-6">
      <div className="page-header">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900 dark:text-white">{title}</h1>
          {subtitle && (
            <p className="text-sm text-secondary-600 dark:text-secondary-400 mt-1">{subtitle}</p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {user?.role === 'project_lead' && typeof onProjectScopeChange === 'function' && (
            <div
              className="inline-flex items-center rounded-lg border border-secondary-200 dark:border-secondary-600 bg-secondary-50 dark:bg-secondary-800 p-0.5"
              role="group"
              aria-label="Project scope"
            >
              <button
                type="button"
                onClick={() => onProjectScopeChange('all')}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  projectScope === 'all'
                    ? 'bg-white dark:bg-secondary-700 text-primary-700 dark:text-primary-300 shadow-sm'
                    : 'text-secondary-600 dark:text-secondary-400 hover:text-secondary-900 dark:hover:text-secondary-200'
                }`}
              >
                <Users className="h-3.5 w-3.5" />
                All projects
              </button>
              <button
                type="button"
                onClick={() => onProjectScopeChange('mine')}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  projectScope === 'mine'
                    ? 'bg-white dark:bg-secondary-700 text-primary-700 dark:text-primary-300 shadow-sm'
                    : 'text-secondary-600 dark:text-secondary-400 hover:text-secondary-900 dark:hover:text-secondary-200'
                }`}
              >
                <User className="h-3.5 w-3.5" />
                My projects
              </button>
            </div>
          )}
          {(user?.role === 'admin' || user?.role === 'project_lead') && (
            <button type="button" onClick={onCreate} className="btn-primary flex items-center gap-2">
              <Plus className="h-4 w-4" />
              New Project
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard icon={FolderOpen} tone="primary" label="Total Projects" value={filteredProjects.length} />
        <StatCard icon={Play} tone="green" label="Active Projects" value={activeCount} />
        <StatCard icon={CheckCircle2} tone="green" label="Completed Projects" value={completedCount} />
      </div>

      {/* Search + Filters — same pattern as Attendance / Team pages */}
      <div className="card">
        <div className="flex items-center gap-4">
          <div className="flex-1 relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-secondary-400" />
            <input
              type="text"
              placeholder="Search by name, description, or client..."
              className="input-field pl-10"
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
            />
          </div>
          {searchTerm && (
            <button
              type="button"
              onClick={() => onSearchChange('')}
              className="text-secondary-400 hover:text-secondary-600"
              title="Clear search"
            >
              <XIcon className="h-4 w-4" />
            </button>
          )}
          <button
            type="button"
            onClick={onToggleFilters}
            className="btn-secondary flex items-center gap-2"
          >
            <FilterIcon className="h-4 w-4" />
            Filters
            <ChevronDown className={`h-4 w-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
          </button>
        </div>

        {showFilters && (
          <div className="mt-4 pt-4 border-t border-secondary-200 dark:border-secondary-700">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
              {renderFilterDropdown(
                'client',
                'Client',
                filters.client,
                filters.client || '',
                (value) => onFilterChange('client', value),
                clientDropdownSearch,
                setClientDropdownSearch,
                filteredClientsForDropdown,
                'All Clients'
              )}
              {renderFilterDropdown(
                'billable',
                'Billable Status',
                filters.is_billable,
                selectedBillable?.name || '',
                (value) => onFilterChange('is_billable', value),
                billableDropdownSearch,
                setBillableDropdownSearch,
                filteredBillableForDropdown,
                'All Projects'
              )}
              {renderFilterDropdown(
                'member',
                'Assigned Member',
                filters.member_id,
                selectedMember
                  ? selectedMember.full_name || selectedMember.username || ''
                  : '',
                (value) => onFilterChange('member_id', value),
                memberDropdownSearch,
                setMemberDropdownSearch,
                filteredMembersForDropdown,
                'All Members'
              )}
              {renderFilterDropdown(
                'documents',
                'Documents',
                filters.has_documents,
                selectedDocuments?.name || '',
                (value) => onFilterChange('has_documents', value),
                documentsDropdownSearch,
                setDocumentsDropdownSearch,
                filteredDocumentsForDropdown,
                'All Projects'
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
              <div>
                <label className="text-xs font-medium text-secondary-600 dark:text-secondary-400 mb-1 block">
                  Min Hourly Rate
                </label>
                <input
                  type="number"
                  className="input-field"
                  placeholder="Min $"
                  value={filters.hourly_rate_min}
                  onChange={(e) => onFilterChange('hourly_rate_min', e.target.value)}
                />
              </div>
              <div>
                <label className="text-xs font-medium text-secondary-600 dark:text-secondary-400 mb-1 block">
                  Max Hourly Rate
                </label>
                <input
                  type="number"
                  className="input-field"
                  placeholder="Max $"
                  value={filters.hourly_rate_max}
                  onChange={(e) => onFilterChange('hourly_rate_max', e.target.value)}
                />
              </div>
              <div>
                <label className="text-xs font-medium text-secondary-600 dark:text-secondary-400 mb-1 block">
                  Created After
                </label>
                <input
                  type="date"
                  className="input-field"
                  value={filters.created_after}
                  onChange={(e) => onFilterChange('created_after', e.target.value)}
                />
              </div>
              <div>
                <label className="text-xs font-medium text-secondary-600 dark:text-secondary-400 mb-1 block">
                  Created Before
                </label>
                <input
                  type="date"
                  className="input-field"
                  value={filters.created_before}
                  onChange={(e) => onFilterChange('created_before', e.target.value)}
                />
              </div>
            </div>

            <div className="mt-4 flex items-center justify-between">
              <div className="text-sm text-secondary-600 dark:text-secondary-400">
                Showing {filteredProjects.length} project{filteredProjects.length !== 1 ? 's' : ''}
                {hasActiveFilters && (
                  <span className="ml-2 px-2 py-1 bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300 rounded-full text-xs">
                    Filtered
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {user?.role === 'admin' && deletedProjectsCount > 0 && (
                  <button
                    type="button"
                    onClick={onToggleDeleted}
                    className="btn-secondary text-sm flex items-center gap-1.5"
                  >
                    <Archive className="h-3.5 w-3.5" />
                    {showDeletedProjects ? 'Active Projects' : `Deleted (${deletedProjectsCount})`}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setOpenFilterDropdown(null);
                    setClientDropdownSearch('');
                    setBillableDropdownSearch('');
                    setMemberDropdownSearch('');
                    setDocumentsDropdownSearch('');
                    onClearFilters();
                  }}
                  className="btn-secondary text-sm"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {showDeletedProjects && user?.role === 'admin' ? (
        <div className="card overflow-hidden p-0">
          <div className="px-4 py-3 border-b border-secondary-200 dark:border-secondary-700 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-red-600 flex items-center gap-2">
              <Archive className="h-4 w-4" />
              Deleted Projects
            </h3>
            <button type="button" onClick={onToggleDeleted} className="btn-secondary text-sm flex items-center gap-1">
              <XIcon className="h-3.5 w-3.5" />
              Back
            </button>
          </div>
          {deletedProjectsLoading ? (
            <div className="py-16 flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
            </div>
          ) : deletedProjects.length === 0 ? (
            <div className="py-16 text-center text-secondary-500">No deleted projects</div>
          ) : (
            <div className="divide-y divide-secondary-200 dark:divide-secondary-700">
              {deletedProjects.map((project) => (
                <div key={project.id} className="px-4 py-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-secondary-900 dark:text-white truncate">{project.name}</p>
                    <p className="text-xs text-secondary-500 truncate">{project.client || 'No client'}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => onRestore(project.id)}
                    className="btn-primary text-sm"
                  >
                    Restore
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : filteredProjects.length === 0 ? (
        <div className="card text-center py-16">
          <FolderOpen className="h-14 w-14 text-secondary-300 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-1">
            {searchTerm
              ? 'No projects match your search'
              : user?.role === 'member' ||
                  (user?.role === 'project_lead' && projectScope === 'mine')
                ? 'No projects for you yet'
                : 'No projects yet'}
          </h3>
          <p className="text-sm text-secondary-500 mb-4">
            {searchTerm
              ? `No projects match "${searchTerm}".`
              : user?.role === 'member' ||
                  (user?.role === 'project_lead' && projectScope === 'mine')
                ? "You haven't created or been added to any projects yet"
                : 'Create your first project to start tracking time'}
          </p>
          {(user?.role === 'admin' || user?.role === 'project_lead') && !searchTerm && (
            <button type="button" onClick={onCreate} className="btn-primary">
              Create Project
            </button>
          )}
        </div>
      ) : (
        <div className="card overflow-hidden p-0">
          {/* Mobile cards */}
          <div className="md:hidden divide-y divide-secondary-200 dark:divide-secondary-700">
            {paginatedProjects.map((project) => {
              const Icon = projectIcon(project.id || project.name);
              const status = projectDisplayStatus(project);
              const progress = projectProgress(project);
              const lastActivity = formatLastActivity(project.updated_at || project.created_at);
              const canManage = canManageProject(project);
              return (
                <div
                  key={project.id}
                  className="p-4 space-y-3 cursor-pointer hover:bg-secondary-50 dark:hover:bg-secondary-800/60"
                  onClick={() => onView(project.id)}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${projectIconTone(project.id || project.name)}`}
                    >
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-secondary-900 dark:text-white truncate">
                        {project.name}
                      </p>
                      <p className="text-xs text-secondary-500 truncate">{project.client || 'No client'}</p>
                    </div>
                    <div onClick={(e) => e.stopPropagation()}>
                      <RowActionsMenu
                        items={[
                          { label: 'Open', icon: Eye, onClick: () => onView(project.id) },
                          { label: 'Open in new tab', icon: ExternalLink, onClick: () => onOpenInNewWindow(project.id) },
                          { label: 'Edit', icon: Edit, onClick: () => onEdit(project), hidden: !canManage },
                          { label: 'Delete', icon: Trash2, onClick: () => onDelete(project.id), danger: true, hidden: !canManage },
                        ]}
                      />
                    </div>
                  </div>
                  <ProgressBar value={progress} />
                  <div className="flex flex-wrap items-center gap-2 justify-between">
                    <StatusPill status={status} />
                    <AvatarStack members={resolveMembers(project.member_ids)} max={3} />
                    <span className="text-xs text-secondary-400">{lastActivity}</span>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full divide-y divide-secondary-200 dark:divide-secondary-700">
              <thead className="bg-secondary-50 dark:bg-secondary-800/50">
                <tr>
                  {['Project', 'Progress', 'Members', 'Status', 'Last Activity', ''].map(
                    (col) => (
                      <th
                        key={col || 'actions'}
                        className="px-4 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider whitespace-nowrap"
                      >
                        {col}
                      </th>
                    )
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-secondary-200 dark:divide-secondary-700 bg-white dark:bg-secondary-800">
                {paginatedProjects.map((project) => {
                  const Icon = projectIcon(project.id || project.name);
                  const status = projectDisplayStatus(project);
                  const progress = projectProgress(project);
                  const lastActivity = formatLastActivity(project.updated_at || project.created_at);
                  const canManage = canManageProject(project);

                  return (
                    <tr
                      key={project.id}
                      onClick={() => onView(project.id)}
                      className="hover:bg-secondary-50 dark:hover:bg-secondary-800/60 cursor-pointer transition-colors"
                    >
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-3 min-w-[200px]">
                          <div
                            className={`h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0 ${projectIconTone(project.id || project.name)}`}
                          >
                            <Icon className="h-4 w-4" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-secondary-900 dark:text-white truncate">
                              {project.name}
                            </p>
                            <p className="text-xs text-secondary-500 truncate">
                              {project.client || 'No client'}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3.5">
                        <ProgressBar value={progress} />
                      </td>
                      <td className="px-4 py-3.5">
                        <AvatarStack members={resolveMembers(project.member_ids)} max={3} />
                      </td>
                      <td className="px-4 py-3.5">
                        <StatusPill status={status} />
                      </td>
                      <td className="px-4 py-3.5 text-sm text-secondary-500 whitespace-nowrap">
                        {lastActivity}
                      </td>
                      <td className="px-4 py-3.5 text-right" onClick={(e) => e.stopPropagation()}>
                        <RowActionsMenu
                          items={[
                            { label: 'Open', icon: Eye, onClick: () => onView(project.id) },
                            {
                              label: 'Open in new tab',
                              icon: ExternalLink,
                              onClick: () => onOpenInNewWindow(project.id),
                            },
                            {
                              label: 'Edit',
                              icon: Edit,
                              onClick: () => onEdit(project),
                              hidden: !canManage,
                            },
                            {
                              label: 'Delete',
                              icon: Trash2,
                              onClick: () => onDelete(project.id),
                              danger: true,
                              hidden: !canManage,
                            },
                          ]}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="px-4 border-t border-secondary-200 dark:border-secondary-700">
            <TablePagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={filteredProjects.length}
              pageSize={pageSize}
              pageSizeOptions={pageSizeOptions}
              onPageChange={onPageChange}
              onPageSizeChange={onPageSizeChange}
              itemLabel="projects"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectsListView;
